import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Building2, Check, ChevronDown, Layers } from 'lucide-react';
import clsx from 'clsx';
import { useBranch } from '../../context/BranchContext';
import { useAuth } from '../../context/AuthContext';

/**
 * Switches the whole product between branches, and carries the organisation's name in the rail.
 *
 * Styled for the dark rail (it used to be a white card — the brightest thing on screen). For a
 * single-branch provider — most are — it is just the organisation's name: a control with one
 * option is clutter that implies something is missing.
 *
 * The menu is portalled and fixed-positioned so the rail's overflow never clips it (in the
 * collapsed 72px rail it used to be cut off).
 */
export default function BranchSwitcher({ collapsed }) {
  const { branches, branchId, branch, setBranchId, multiBranch } = useBranch();
  const { user } = useAuth();
  const orgName = user?.orgName || '';
  const [pos, setPos] = useState(null); // where the open menu sits; null = closed
  const btnRef = useRef(null);
  const menuRef = useRef(null);
  const open = !!pos;

  const close = useCallback((focusButton) => {
    setPos(null);
    if (focusButton && btnRef.current) btnRef.current.focus();
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    const away = (e) => {
      if (btnRef.current?.contains(e.target) || menuRef.current?.contains(e.target)) return;
      close(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') { e.preventDefault(); close(true); } };
    const onResize = () => close(false);
    document.addEventListener('mousedown', away);
    document.addEventListener('keydown', onKey);
    window.addEventListener('resize', onResize);
    return () => {
      document.removeEventListener('mousedown', away);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('resize', onResize);
    };
  }, [open, close]);

  // Opening moves focus to the current choice, so arrow keys work straight away.
  useEffect(() => {
    if (!open || !menuRef.current) return;
    const current = menuRef.current.querySelector('[aria-checked="true"]')
      || menuRef.current.querySelector('[role="menuitemradio"]');
    if (current) current.focus();
  }, [open]);

  if (!multiBranch) {
    if (!orgName || collapsed) return null;
    return (
      <div className="px-3 py-2 rounded-lg border border-white/10 bg-white/5">
        <span className="block text-[10px] font-semibold uppercase tracking-[0.08em] text-slate-400">Organisation</span>
        <span className="block text-[13px] font-semibold text-slate-200 truncate">{orgName}</span>
      </div>
    );
  }

  const label = branch ? branch.name : 'All branches';

  const toggle = () => {
    if (open) { close(false); return; }
    const r = btnRef.current.getBoundingClientRect();
    const width = Math.max(r.width, 240);
    setPos({ left: Math.min(r.left, window.innerWidth - width - 8), top: r.bottom + 4, width });
  };

  const onMenuKeyDown = (e) => {
    const items = Array.from(menuRef.current?.querySelectorAll('[role="menuitemradio"]') || []);
    if (!items.length) return;
    const i = items.indexOf(document.activeElement);
    const go = (n) => { e.preventDefault(); items[(n + items.length) % items.length].focus(); };
    if (e.key === 'ArrowDown') go(i + 1);
    else if (e.key === 'ArrowUp') go(i - 1);
    else if (e.key === 'Home') go(0);
    else if (e.key === 'End') go(items.length - 1);
    else if (e.key === 'Tab') close(false);
  };

  const pick = (id) => {
    close(true);
    if ((branchId || '') !== (id || '')) setBranchId(id);
  };

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        onClick={toggle}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={collapsed ? `Branch: ${label}. Change branch` : undefined}
        title={collapsed ? label : undefined}
        className={clsx(
          'w-full flex items-center gap-2 rounded-lg border border-white/10 bg-white/5 text-slate-200 hover:bg-white/10 transition-colors duration-150',
          collapsed ? 'justify-center px-2 py-2' : 'px-3 py-2'
        )}
      >
        {branch
          ? <Building2 size={15} className="shrink-0 text-primary-300" aria-hidden="true" />
          : <Layers size={15} className="shrink-0 text-slate-400" aria-hidden="true" />}
        {!collapsed && (
          <>
            <span className="flex-1 min-w-0 text-left">
              <span className="block text-[10px] font-semibold uppercase tracking-[0.08em] text-slate-400 truncate">
                {orgName || 'Branch'}
              </span>
              <span className={clsx('block text-[13px] font-semibold truncate', branch ? 'text-primary-300' : 'text-slate-200')}>
                {label}
              </span>
            </span>
            <ChevronDown size={14} aria-hidden="true"
              className={clsx('shrink-0 text-slate-400 transition-transform duration-150', open && 'rotate-180')} />
          </>
        )}
      </button>

      {open && createPortal(
        <div
          ref={menuRef}
          role="menu"
          aria-label="Switch branch"
          onKeyDown={onMenuKeyDown}
          style={{ position: 'fixed', left: pos.left, top: pos.top, width: pos.width, zIndex: 70 }}
          className="rounded-xl border border-slate-200 bg-white shadow-lg py-1 max-h-80 overflow-y-auto"
        >
          {orgName && (
            <p className="px-3 py-1.5 text-[10.5px] font-semibold uppercase tracking-[0.08em] text-slate-500 truncate">
              {orgName}
            </p>
          )}
          <Row
            label="All branches"
            hint="Everything across the organisation"
            active={!branchId}
            onClick={() => pick('')}
          />
          <div className="my-1 border-t border-slate-100" role="separator" />
          {branches.map(b => (
            <Row
              key={b.id}
              label={b.name}
              hint={b.code || b.address || ''}
              active={branchId === b.id}
              onClick={() => pick(b.id)}
            />
          ))}
        </div>,
        document.body
      )}
    </>
  );
}

function Row({ label, hint, active, onClick }) {
  return (
    <button
      type="button"
      role="menuitemradio"
      aria-checked={active}
      tabIndex={-1}
      onClick={onClick}
      className={clsx(
        'w-full flex items-start gap-2 px-3 py-2 text-left transition-colors duration-150 focus:outline-none focus-visible:bg-slate-100',
        active ? 'bg-primary-50' : 'hover:bg-slate-50'
      )}
    >
      <span className="w-4 pt-0.5 shrink-0">
        {active && <Check size={14} className="text-primary-600" aria-hidden="true" />}
      </span>
      <span className="min-w-0">
        <span className={clsx('block text-sm font-semibold truncate', active ? 'text-primary-700' : 'text-slate-700')}>
          {label}
        </span>
        {hint && <span className="block text-[11px] text-slate-500 truncate">{hint}</span>}
      </span>
    </button>
  );
}
